import { createSelector } from "@reduxjs/toolkit";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { formatIndividual, getDependentEnrollment } from "EmployeeApplication/selectors";
import { employeePolicyProcessStrategy } from "EntitiesApi/schemas";
import employeeHashId from "Utils/employeeHashID";
import { fetchWithCSRFTokenForRequestObject } from "Utils/fetchWithCSRFToken";
import {
  HealthAccountBenefitOverview,
  AccidentPlan,
  LifePlan,
  TermLifePlan,
  WholeLifePlan,
  ShortTermDisabilityPlan,
  LongTermDisabilityPlan,
  Plan,
  PlanCost,
} from "Utils/types";

import { appendAuthParams } from "Common/TokenAuth/Utils";

import {
  basicInformationURL,
  familyInformationURL,
  chooseMedicalPlanURL,
  chooseDentalPlanURL,
  chooseVisionPlanURL,
  chooseAccidentPlanURL,
  chooseLifePlanURL,
  chooseTermLifePlanURL,
  chooseWholeLifePlanURL,
  chooseShortTermDisabilityPlanURL,
  chooseLongTermDisabilityPlanURL,
  additionalInformationURL,
  signURL,
  doneURL,
  isRenewal,
  healthAccountInformationURL,
} from "./urls";

import type { UpdateFamilyInformationRequest, UpdateFamilyInformationResponse } from "./types";

interface GetPlansResponse {
  plans: Array<Plan | AccidentPlan | LifePlan | TermLifePlan | WholeLifePlan | ShortTermDisabilityPlan | LongTermDisabilityPlan>;
}
interface GetPlansArgs {
  id: string | number;
  type: "medical" | "dental" | "vision" | "accident" | "life" | "term_life" | "whole_life" | "short_term_disability" | "long_term_disability";
}
interface GetPlanCostsResponse {
  planCosts: PlanCost[];
}
interface GetPlanCostsArgs {
  id: string | number;
  type: "medical" | "dental" | "vision" | "accident" | "life" | "term_life" | "whole_life" | "short_term_disability" | "long_term_disability";
  dependentIDs?: Array<string | number>;
  coverageAmount?: number;
  spouseCoverageAmount?: number;
}
interface EmployeePolicyIdArgs {
  id: string | number;
}
interface HealthAccountBenefitOverviewResponse {
  healthAccountBenefitOverview: HealthAccountBenefitOverview;
}

export const employeeApplicationApi = createApi({
  reducerPath: "employeeApplicationApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/v1/",
    fetchFn: fetchWithCSRFTokenForRequestObject,
  }),

  endpoints: (builder) => ({
    getApplication: builder.query<any, void>({
      query: () => {
        const hash_id = employeeHashId();
        const data = {
          ...(hash_id ? { employee_hash_id: hash_id } : {}),
        };

        const params = new URLSearchParams(data).toString();
        return {
          url: appendAuthParams(`employee_application/show_hashed_employee_policy?${params}`),

          method: "post",
        };
      },
      transformResponse: (response, _meta, _arg) => {
        const employeeApplication = response.employeePolicy;
        return {
          employeePolicy: employeePolicyProcessStrategy(employeeApplication),
          adminMode: response.adminMode,
          benefitsAdmin: response.benefitsAdmin,
          healthAccountBenefitStatus: response.healthAccountBenefitStatus,
          linkedAccount: response.linkedAccount,
          companyIDToken: response.companyIDToken,
          companyProfileID: response.companyProfileId,
          employeeFormId: response.employeeFormId,
          employeeFormID: response.employeeFormId,
          companyContribution: response.contributions,
          availablePlans: response.availablePlans || [],
          abTests: response.abTests || {},
          siteConfig: {
            branding: response.siteConfig?.branding || "default",
            partnerLogo: response.siteConfig?.partner_logo,
            partnerName: response.siteConfig?.partner_name,
          },
        };
      },
      providesTags: ["Application"],
    }),
    getPlans: builder.query<GetPlansResponse, GetPlansArgs>({
      query: ({ id, type }) => {
        const params = new URLSearchParams({
          id: id.toString(),
          type,
        }).toString();
        return appendAuthParams(`employee_application/plans?${params}`);
      },
      providesTags: (_result, _error, arg) => ["Plans", { type: "Plans", id: arg.type }],
    }),
    getPlanCosts: builder.query<GetPlanCostsResponse, GetPlanCostsArgs>({
      query: ({ dependentIDs, id, type, coverageAmount, spouseCoverageAmount }) => {
        const params = new URLSearchParams({ id: id.toString(), type });
        if (dependentIDs?.length === 0) {
          params.append("dependentIDs", "");
        } else if (dependentIDs?.length > 0) {
          params.append("dependentIDs", dependentIDs.map((depID) => depID.toString()).join(","));
        }
        if (coverageAmount) {
          params.append("coverageAmount", coverageAmount.toString());
        }
        if (spouseCoverageAmount) {
          params.append("spouseCoverageAmount", spouseCoverageAmount.toString());
        }
        return appendAuthParams(`employee_application/plan_costs?${params.toString()}`);
      },
      providesTags: ["PlanCosts"],
    }),
    getOfferedPlans: builder.query({
      query: (body) => ({
        url: appendAuthParams("employee_application/quote"),
        method: "post",
        body: { ...body },
      }),

      transformResponse: (response, _meta, arg) => {
        const type = arg.type || "medical";
        const prices = response.quotes;

        const plansArray = type === "medical" ? response.plans : response[`${type}Plans`];
        // Transform plans array into expected object shape where id is key, plan is value
        const plans = plansArray.reduce((result, item) => {
          result[item.id] = item;
          return result;
        }, {});

        return { prices, plans };
      },
      providesTags: (_result, _error, arg) => ["offeredPlans", { type: "offeredPlans", id: arg.type }],
    }),

    updateEmployeeForm: builder.mutation({
      query: (body) => ({
        url: appendAuthParams("employee_application/update_employee_form"),
        method: "post",
        body: { ...body },
      }),
      invalidatesTags: ["Application", "PlanCosts"],
    }),

    updateEmployeePolicy: builder.mutation({
      query: (body) => ({
        url: appendAuthParams("employee_application/update_employee_policy"),
        method: "post",
        body: { ...body },
      }),
      invalidatesTags: ["Application"],
    }),

    updateFamilyInformation: builder.mutation<UpdateFamilyInformationResponse, UpdateFamilyInformationRequest>({
      query: (body) => ({
        url: appendAuthParams("employee_application/update_family_information"),
        method: "post",
        body: { ...body },
      }),
      invalidatesTags: ["Application", "offeredPlans", "PlanCosts"],
    }),

    updateSignature: builder.mutation({
      query: (body, isRenewal) => ({
        url: appendAuthParams("employee_application/sign_application"),
        method: "post",
        body: { ...body, is_renewal: isRenewal },
      }),
      invalidatesTags: ["Application"],
    }),

    getHealthAccountBenefitOverview: builder.query<HealthAccountBenefitOverviewResponse, EmployeePolicyIdArgs>({
      query: ({ id }) => {
        const params = new URLSearchParams({
          id: id.toString(),
        }).toString();
        return appendAuthParams(`employee_application/health_account_benefit_overview?${params}`);
      },
    }),
  }),

  tagTypes: ["Application", "offeredPlans", "Plans", "PlanCosts"],
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetApplicationQuery,
  useGetHealthAccountBenefitOverviewQuery,
  useGetPlansQuery,
  useGetPlanCostsQuery,
  useGetOfferedPlansQuery,
  useUpdateEmployeeFormMutation,
  useUpdateEmployeePolicyMutation,
  useUpdateFamilyInformationMutation,
  useUpdateSignatureMutation,
} = employeeApplicationApi;

const selectEmployeeApplicationResult = employeeApplicationApi.endpoints.getApplication.select();
const getEmployeeApplicationData = createSelector(selectEmployeeApplicationResult, (employeeApplicationResult) => employeeApplicationResult?.data || {});

export const getEmployeeApplication = createSelector(getEmployeeApplicationData, (employeeApplicationData) => employeeApplicationData.employeePolicy);

export const getEmployeeForm = createSelector(getEmployeeApplication, (employeeApplication) => employeeApplication?.employeeForm);

export const getDependents = createSelector(getEmployeeForm, (employeeForm) => employeeForm?.employeeDependents?.filter((d) => d?.active) || []);

export const getFamilyInfo = createSelector(getEmployeeForm, getDependents, (employeeForm, dependents) => [employeeForm, ...dependents].map(formatIndividual));

export const getCompanyContribution = createSelector(getEmployeeApplicationData, (employeeApplicationData) => employeeApplicationData.companyContribution);

export const getAvailablePlans = createSelector(getEmployeeApplicationData, (employeeApplicationData) => employeeApplicationData.availablePlans || []);

export const getHealthAccountBenefitStatus = createSelector(
  getEmployeeApplicationData,
  (employeeApplicationData) => employeeApplicationData.healthAccountBenefitStatus || false,
);

const getDependentEnrollmentMedical = createSelector(getEmployeeApplication, (employeeApplication) => getDependentEnrollment(employeeApplication, "medical"));

const getDependentEnrollmentDental = createSelector(getEmployeeApplication, (employeeApplication) => getDependentEnrollment(employeeApplication, "dental"));

const getDependentEnrollmentVision = createSelector(getEmployeeApplication, (employeeApplication) => getDependentEnrollment(employeeApplication, "vision"));

export const getEnrollingDependents = createSelector(
  getDependentEnrollmentMedical,
  getDependentEnrollmentDental,
  getDependentEnrollmentVision,
  getDependents,
  (medicalDeps, dentalDeps, visionDeps, deps) =>
    deps.filter((dep) => medicalDeps[dep.id] === "Yes" || dentalDeps[dep.id] === "Yes" || visionDeps[dep.id] === "Yes"),
);

export const getAdminMode = createSelector(selectEmployeeApplicationResult, (employeeApplicationResult) => employeeApplicationResult?.data?.adminMode);

export const getBenefitsAdmin = createSelector(selectEmployeeApplicationResult, (employeeApplicationResult) => employeeApplicationResult?.data?.benefitsAdmin);

export const getCompanyIDToken = createSelector(
  selectEmployeeApplicationResult,
  (employeeApplicationResult) => employeeApplicationResult?.data?.companyIDToken,
);

export const getCompanyProfileID = createSelector(
  selectEmployeeApplicationResult,
  (employeeApplicationResult) => employeeApplicationResult?.data?.companyProfileID,
);

export const getSiteConfig = createSelector(selectEmployeeApplicationResult, (employeeApplicationResult) => employeeApplicationResult?.data?.siteConfig);

// Each boolean is true if the step is completed or skipped, false otherwise.
// chooseDental and chooseVision plan declination should be marked as complete
// if they decline medical since we don't allow them to only take dental/vision
export const getProcessOverviewData = createSelector(
  [getEmployeeApplication, getEmployeeForm, getAvailablePlans, getHealthAccountBenefitStatus],
  (employeePolicy, employeeForm, availablePlans, healthAccountBenefitStatus): ProcessOverviewData => ({
    basicInformation: !!(employeeForm && employeeForm.firstName && employeeForm.zipCode && employeeForm.maritalStatus),

    familyInformation: !!(employeePolicy && employeePolicy.completedFamilyDetails),

    chooseMedicalPlan: !!(employeePolicy && employeePolicy.isEnrolling),
    chooseDentalPlan: availablePlans.indexOf("dental") === -1 || !!(employeePolicy && (employeePolicy.isEnrollingDental || employeePolicy.isEnrolling == "No")),
    chooseVisionPlan: availablePlans.indexOf("vision") === -1 || !!(employeePolicy && (employeePolicy.isEnrollingVision || employeePolicy.isEnrolling == "No")),
    chooseAccidentPlan:
      availablePlans.indexOf("accident") === -1 || !!(employeePolicy && (employeePolicy.isEnrollingAccident || employeePolicy.isEnrolling == "No")),
    chooseLifePlan: availablePlans.indexOf("life") === -1 || !!(employeePolicy && (employeePolicy.isEnrollingLife || employeePolicy.isEnrolling == "No")),
    chooseTermLifePlan:
      availablePlans.indexOf("term_life") === -1 || !!(employeePolicy && (employeePolicy.isEnrollingTermLife || employeePolicy.isEnrolling == "No")),
    chooseWholeLifePlan:
      availablePlans.indexOf("whole_life") === -1 || !!(employeePolicy && (employeePolicy.isEnrollingWholeLife || employeePolicy.isEnrolling == "No")),
    chooseShortTermDisabilityPlan:
      availablePlans.indexOf("short_term_disability") === -1 ||
      !!(employeePolicy && (employeePolicy.isEnrollingShortTermDisability || employeePolicy.isEnrolling == "No")),
    chooseLongTermDisabilityPlan:
      availablePlans.indexOf("long_term_disability") === -1 ||
      !!(employeePolicy && (employeePolicy.isEnrollingLongTermDisability || employeePolicy.isEnrolling == "No")),
    healthAccountBenefits: healthAccountBenefitStatus !== "incomplete",
    additionalInformation: !!(employeeForm && employeeForm.jobTitle),
    sign: !!(employeePolicy && employeePolicy.signatureImageDataUrl && employeeForm && employeeForm.socialSecurityNumber),
  }),
);

export const getFirstIncompleteURL = createSelector([getProcessOverviewData, getAdminMode], (processOverview, adminMode) => {
  const hashId = employeeHashId();
  if (!processOverview.basicInformation) {
    return basicInformationURL(adminMode, isRenewal(), hashId);
  }
  if (!processOverview.familyInformation) {
    return familyInformationURL(adminMode, isRenewal(), hashId);
  }
  if (!processOverview.chooseMedicalPlan) {
    return chooseMedicalPlanURL(adminMode, isRenewal(), hashId);
  }
  if (!processOverview.chooseDentalPlan) {
    return chooseDentalPlanURL(adminMode, isRenewal(), hashId);
  }
  if (!processOverview.chooseVisionPlan) {
    return chooseVisionPlanURL(adminMode, isRenewal(), hashId);
  }
  if (!processOverview.chooseAccidentPlan) {
    return chooseAccidentPlanURL(adminMode, isRenewal(), hashId);
  }
  if (!processOverview.chooseLifePlan) {
    return chooseLifePlanURL(adminMode, isRenewal(), hashId);
  }
  if (!processOverview.chooseTermLifePlan) {
    return chooseTermLifePlanURL(adminMode, isRenewal(), hashId);
  }
  if (!processOverview.chooseWholeLifePlan) {
    return chooseWholeLifePlanURL(adminMode, isRenewal(), hashId);
  }
  if (!processOverview.chooseShortTermDisabilityPlan) {
    return chooseShortTermDisabilityPlanURL(adminMode, isRenewal(), hashId);
  }
  if (!processOverview.chooseLongTermDisabilityPlan) {
    return chooseLongTermDisabilityPlanURL(adminMode, isRenewal(), hashId);
  }
  if (!processOverview.additionalInformation) {
    return additionalInformationURL(adminMode, false, hashId);
  }
  if (!processOverview.healthAccountBenefits) {
    return healthAccountInformationURL(adminMode, isRenewal(), hashId);
  }
  if (!processOverview.sign) {
    return signURL(adminMode, isRenewal(), hashId);
  }
  return doneURL(adminMode, isRenewal(), hashId);
});
